/* Eve's Cycle — persistent 3D background scene (vanilla three.js port of the R3F scene).
   Unlit billboards, no post-processing. Scroll-scrubbed + cursor repel.
   Bottles dock into the 01/02/03 feature slots; non-active bottles blur out. */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.169.0/build/three.module.js';

const CFG = window.EC3D_CONFIG || {};
const MOUNT = document.getElementById('ec3d-scene');
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Desktop only — matches the React build (mobile gets the CSS gradient alone).
if (MOUNT && !reduced && window.innerWidth >= 900) {
  const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
  const ss = (a, b, x) => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };

  // dock windows: gradual in, long HOLD, gradual out (3rd holds longest)
  const IN0 = [0.03, 0.37, 0.64], IN1 = [0.14, 0.48, 0.74];
  const HLD = [0.29, 0.62, 0.92], OUT = [0.40, 0.73, 0.99];
  const featAmount = (p, i) => ss(IN0[i], IN1[i], p) * (1 - ss(HLD[i], OUT[i], p));

  const LAYOUT = [
    { pos: [2.3, 0.0, 0.5],  scale: 4.6, phase: 0.0, drift: 1.4, depth: 1.0 },
    { pos: [5.0, 1.3, -1.6], scale: 3.5, phase: 1.9, drift: 0.9, depth: 0.55 },
    { pos: [4.0, -1.7, -0.8],scale: 3.7, phase: 3.4, drift: 1.1, depth: 0.8 }
  ];
  const SLOTS = [
    { x: -2.35, y: 0.15, z: 1.1, scale: 4.3 },
    { x:  2.55, y: 0.15, z: 1.1, scale: 4.3 },
    { x: -2.35, y: 0.15, z: 1.1, scale: 4.3 }
  ];
  const GLOW_COLORS = ['#33c6d6', '#e28ac6', '#ff5f88'];
  const CAPSULE_COUNT = 14;

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  // Render at the display's real density (capped) so the bottles stay crisp
  // on retina screens instead of being upscaled from a 1x buffer.
  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  renderer.setPixelRatio(DPR);
  renderer.setSize(window.innerWidth, window.innerHeight);
  MOUNT.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(44, window.innerWidth / window.innerHeight, 0.1, 100);
  camera.position.set(0, 0, 8.2);

  const maxAniso = renderer.capabilities.getMaxAnisotropy ? renderer.capabilities.getMaxAnisotropy() : 8;

  // soft radial glow sprite
  function glowTexture() {
    const c = document.createElement('canvas'); c.width = c.height = 128;
    const ctx = c.getContext('2d');
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.42)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, 128, 128);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
  }
  const GLOW = glowTexture();

  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin('anonymous');
  const load = (url) => new Promise((res) => {
    if (!url) return res(null);
    loader.load(url, (t) => {
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = maxAniso;             // crisp at glancing angles
      t.minFilter = THREE.LinearMipmapLinearFilter;
      t.magFilter = THREE.LinearFilter;
      t.generateMipmaps = true;
      t.needsUpdate = true;
      res(t);
    }, undefined, () => res(null));
  });

  const bottles = [];
  const capsules = [];
  const sharpN = [1, 1, 1];
  const tilt = [{x:0,y:0},{x:0,y:0},{x:0,y:0}];

  const cursor = { x: 0, y: 0 };
  window.addEventListener('pointermove', (e) => {
    cursor.x = (e.clientX / window.innerWidth) * 2 - 1;
    cursor.y = -((e.clientY / window.innerHeight) * 2 - 1);
  }, { passive: true });

  function cursorWorld(z) {
    const halfH = Math.tan((camera.fov * Math.PI) / 360) * (camera.position.z - z);
    const halfW = halfH * (window.innerWidth / window.innerHeight);
    return [camera.position.x + cursor.x * halfW, camera.position.y + cursor.y * halfH];
  }

  function plane(tex, opacity) {
    const aspect = tex && tex.image ? tex.image.width / tex.image.height : 0.746;
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, opacity: opacity,
      depthWrite: false, toneMapped: false
    });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(aspect, 1), mat);
    return { mesh: m, mat: mat };
  }

  async function build() {
    const sharpTex = await Promise.all((CFG.bottles || []).map(load));
    const blurTex = await Promise.all((CFG.bottlesBlur || []).map(load));
    const capTex = (await Promise.all((CFG.capsules || []).map(load))).filter(Boolean);

    for (let i = 0; i < 3; i++) {
      if (!sharpTex[i]) continue;
      const L = LAYOUT[i];
      const g = new THREE.Group();
      g.position.set(L.pos[0], L.pos[1], L.pos[2]);
      g.scale.setScalar(L.scale);

      const col = new THREE.Color(GLOW_COLORS[i]);
      const sh = new THREE.Mesh(new THREE.PlaneGeometry(0.746, 1),
        new THREE.MeshBasicMaterial({ map: GLOW, color: 0x3a0a16, transparent: true, opacity: 0.26, depthWrite: false }));
      sh.position.set(0.04, -0.62, -0.7); sh.scale.set(1.15, 0.42, 1); g.add(sh);
      const gw = new THREE.Mesh(new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({ map: GLOW, color: col, transparent: true, opacity: 0.44, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }));
      gw.position.set(0, 0.06, -0.45); gw.scale.set(2.15, 2.55, 1); g.add(gw);
      const gc = new THREE.Mesh(new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({ map: GLOW, color: col, transparent: true, opacity: 0.31, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }));
      gc.position.set(0, 0.02, -0.2); gc.scale.set(1.2, 1.55, 1); g.add(gc);

      let blurMat = null;
      if (blurTex[i]) {
        const b = plane(blurTex[i], 0);
        b.mesh.position.z = -0.01; b.mesh.scale.setScalar(1.06);
        g.add(b.mesh); blurMat = b.mat;
      }
      const s = plane(sharpTex[i], 1);
      g.add(s.mesh);

      scene.add(g);
      bottles.push({ group: g, sharpMat: s.mat, blurMat: blurMat, L: L });
    }

    if (capTex.length) {
      for (let i = 0; i < CAPSULE_COUNT; i++) {
        const depth = 0.35 + Math.random() * 0.85;
        const d = {
          pos: [(Math.random() - 0.5) * 13, (Math.random() - 0.5) * 13, (Math.random() - 0.5) * 4 - 1],
          rot: (Math.random() - 0.5) * 1.6,
          spin: (Math.random() - 0.5) * 0.16,
          speed: 0.22 + Math.random() * 0.4,
          depth: depth,
          scale: (0.82 + Math.random() * 0.55) * (0.82 + depth * 0.34)
        };
        const tex = capTex[i % capTex.length];
        const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1),
          new THREE.MeshBasicMaterial({ map: tex, transparent: true, alphaTest: 0.04, depthWrite: false, toneMapped: false }));
        m.position.set(d.pos[0], d.pos[1], d.pos[2]);
        m.rotation.z = d.rot; m.scale.setScalar(d.scale);
        scene.add(m);
        capsules.push({ mesh: m, d: d, rep: { x: 0, y: 0 } });
      }
    }
    loop();
  }

  const ff = { active: false, p: 0 };
  let pageP = 0;
  function readScroll() {
    const doc = document.documentElement;
    const max = Math.max(1, doc.scrollHeight - window.innerHeight);
    pageP = clamp01(window.scrollY / max);
    const el = document.querySelector('[data-ec3d-feat]');
    if (el) {
      const r = el.getBoundingClientRect();
      const span = Math.max(1, el.offsetHeight - window.innerHeight);
      ff.p = clamp01(-r.top / span);
      ff.active = r.top < window.innerHeight && r.bottom > 0;
    } else { ff.active = false; }
  }
  window.addEventListener('scroll', readScroll, { passive: true });
  window.addEventListener('resize', () => {
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    readScroll();
  });
  readScroll();

  let visible = true;
  document.addEventListener('visibilitychange', () => { visible = !document.hidden; });

  const clock = new THREE.Clock();
  function loop() {
    requestAnimationFrame(loop);
    if (!visible) return;
    const t = clock.getElapsedTime();
    const p = pageP, mx = cursor.x, my = cursor.y;

    for (let i = 0; i < bottles.length; i++) {
      const B = bottles[i], L = B.L, g = B.group;
      let fx = L.pos[0] - p * L.drift * 0.22 + Math.sin(t * 0.3 + L.phase) * 0.12 + mx * 0.55 * L.depth;
      let fy = L.pos[1] + Math.sin(t * 0.5 + L.phase) * 0.22 + p * 0.7 * L.depth + my * 0.4 * L.depth;
      const fa = ff.active ? featAmount(ff.p, i) : 0;

      if (fa < 0.995) {
        const cw = cursorWorld(L.pos[2]);
        const dx = fx - cw[0], dy = fy - cw[1];
        const dist = Math.hypot(dx, dy) || 0.0001, R = 3.0;
        if (dist < R) { const f = 1 - dist / R, push = f * f * 1.7 * (1 - fa);
          fx += (dx / dist) * push; fy += (dy / dist) * push; }
      }

      const S = SLOTS[i];
      const tx = fx + (S.x + mx * 0.12 - fx) * fa;
      const ty = fy + (S.y + my * 0.08 - fy) * fa;
      const tz = L.pos[2] + (S.z - L.pos[2]) * fa;
      const tscale = L.scale + (S.scale - L.scale) * fa;
      const ease = 0.05 + Math.min(fa, 1) * 0.06;
      g.position.x += (tx - g.position.x) * ease;
      g.position.y += (ty - g.position.y) * ease;
      g.position.z += (tz - g.position.z) * ease;
      g.scale.setScalar(g.scale.x + (tscale - g.scale.x) * ease);

      if (!ff.active) sharpN[i] = 1;
      else sharpN[i] += (clamp01(fa * 2.4) - sharpN[i]) * 0.1;
      if (B.sharpMat) B.sharpMat.opacity = sharpN[i];
      if (B.blurMat) B.blurMat.opacity = (1 - sharpN[i]) * 0.92;

      const damp = 1 - fa * 0.85;
      const tgtY = (mx * 0.42 + Math.sin(t * 0.25 + L.phase) * 0.07) * damp;
      const tgtX = (-my * 0.32 + Math.sin(t * 0.3 + L.phase) * 0.04) * damp;
      const s = tilt[i];
      s.y += (tgtY - s.y) * 0.055; s.x += (tgtX - s.x) * 0.055;
      g.rotation.y = s.y; g.rotation.x = s.x;
      g.rotation.z = Math.sin(t * 0.32 + L.phase) * 0.02 * damp;
    }

    for (let i = 0; i < capsules.length; i++) {
      const C = capsules[i], d = C.d, m = C.mesh;
      const rise = ((d.pos[1] + p * d.depth * 6 + 6.5) % 13) - 6.5;
      const bx = d.pos[0] + Math.sin(t * 0.18 + i) * 0.35;
      const by = rise + Math.sin(t * d.speed + i) * 0.45;
      const cw = cursorWorld(d.pos[2]);
      let dx = bx - cw[0], dy = by - cw[1];
      const dist = Math.hypot(dx, dy) || 0.0001, R = 2.7;
      let ox = 0, oy = 0;
      if (dist < R) { const f = 1 - dist / R, push = f * f * 2.4 * d.depth;
        ox = (dx / dist) * push; oy = (dy / dist) * push; }
      C.rep.x += (ox - C.rep.x) * 0.13;
      C.rep.y += (oy - C.rep.y) * 0.13;
      m.position.set(bx + C.rep.x, by + C.rep.y, d.pos[2]);
      m.rotation.z = d.rot + Math.sin(t * 0.24 + i) * 0.1 + t * d.spin;
    }

    const cz = 8.4 - p * 1.0;
    camera.position.z += (cz - camera.position.z) * 0.05;
    camera.position.x += (cursor.x * 0.6 - camera.position.x) * 0.03;
    camera.position.y += (cursor.y * 0.4 - camera.position.y) * 0.03;
    camera.lookAt(0, 0, 0);

    renderer.render(scene, camera);
  }

  MOUNT.style.opacity = '0';
  MOUNT.style.transition = 'opacity 1s ease';
  build().then(() => { requestAnimationFrame(() => { MOUNT.style.opacity = '1'; }); });
}
